import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import authReducer from './slices/auth/slice';
import moveResultModalReducer from './slices/moveResultModalSlice';
import { baseAPI } from 'services/baseApi';
import customizationReducer from './customizationReducer';

const store = configureStore({
  reducer: {
    auth: authReducer,
    customization: customizationReducer,
    moveResultModal: moveResultModalReducer,
    [baseAPI.reducerPath]: baseAPI.reducer
  },
  middleware: (defaultMiddleware) => defaultMiddleware().concat(baseAPI.middleware)
});

// Subscribes the API to tab-visibility and online/offline events. Every query
// then refetches when you come back to this tab, which is what stops the panel
// showing stale numbers after you have been somewhere else. Polling alone does
// not cover it: browsers throttle timers in background tabs down to once a
// minute, and harder still after a few minutes, so returning to a polled page
// can show data that is minutes old until the next tick.
//
// This replaces the default listeners rather than using them, to drop the one
// they add for the window's `focus` event. A window.confirm() blurs the window
// and focuses it again when you answer, so with that listener EVERY confirm-
// then-delete in the panel fired a refetch at the same moment as the delete
// itself. The read could be answered with the row still present and land after
// the delete, putting the deleted row back on screen. Tab visibility is the
// signal actually wanted here, and a dialog does not change it.
setupListeners(store.dispatch, (dispatch, actions) => {
  const handleOnline = () => dispatch(actions.onOnline());
  const handleOffline = () => dispatch(actions.onOffline());
  const handleVisibility = () => {
    if (document.visibilityState === 'visible') dispatch(actions.onFocus());
    else dispatch(actions.onFocusLost());
  };
  window.addEventListener('online', handleOnline, false);
  window.addEventListener('offline', handleOffline, false);
  document.addEventListener('visibilitychange', handleVisibility, false);
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
});

export const useAppDispatch = useDispatch;
export const useAppSelector = useSelector;
export default store;
